import { Bus, useAside, useConfirm, useLoader, useModal, useNotif } from '@orion.ui/orion';
import { debounce, orderBy, uniqBy } from 'lodash-es';
import { reactive } from 'vue';
import { getWeekDays, idsQuery } from '@/utils/tools';
import { SETTING_TYPE } from '@/typings/enums/SETTING_TYPE';
import { DOCUMENT_TYPE } from '@/typings/enums/DOCUMENT_TYPE';
import { USER_TYPE } from '@/typings/enums/USER_TYPE';
import router from '@/router/index.router';
import GouvApi from '@/api/GouvApi';
import BaseService from '../BaseService';
import TimeTicketAttachmentAside from '@/components/time-ticket/TimeTicketAttachmentAside.vue';
import TimeTicketMissionEndModal from '@/components/time-ticket/TimeTicketMissionEndModal.vue';
import TimeTicketWeekConfirmModal from '@/components/time-ticket/TimeTicketWeekConfirmModal.vue';
import TimeTicketWeekApiTempWorker from '@/api/TimeTicketWeekApiTempWorker';
import TimeTicketWeekApiClient from '@/api/TimeTicketWeekApiClient';
import TimeTicketWeekApiAgency from '@/api/TimeTicketWeekApiAgency';
import useEmailService from '../EmailService';
import type TimeTicketWeekEntity from '@/entity/time-ticket/TimeTicketWeekEntity';
import type TimeTicketWeekTempWorkerEntity from '@/entity/time-ticket/TimeTicketWeekTempWorkerEntity';

class TimeTicketWeekService extends BaseService {

	private readonly serviceState = reactive({
		dayNight: false,
		showFlex: false,
		hoursMinutes: localStorage.getItem('RH__hoursMinutes') === 'true' ? true : false,
		unfold: false,
		tagSelectOpen: false,
		bonusLoadingId: null as Nullable<number>,
		weekNumber: undefined as Undef<number>,
		year: undefined as Undef<number>,
		holidays: {} as ApiGouvHolidays,
	});


	debouncedNotif = debounce(() => {
		useNotif.success(`Modifications sauvegardées !`);
	}, 1500);

	get dayNight () { return this.serviceState.dayNight; }
	set dayNight (val) { this.serviceState.dayNight = val; }

	get hoursMinutes () { return this.serviceState.hoursMinutes; }
	set hoursMinutes (val) { this.serviceState.hoursMinutes = val; localStorage.setItem('RH__hoursMinutes', val.toString()); }

	get showFlex () { return this.serviceState.showFlex; }
	set showFlex (val) { this.serviceState.showFlex = val; }

	get unfold () { return this.serviceState.unfold; }
	set unfold (val) { this.serviceState.unfold = val; }

	get tagSelectOpen () { return this.serviceState.tagSelectOpen; }
	set tagSelectOpen (val) { this.serviceState.tagSelectOpen = val; }

	get weekNumber () { return this.serviceState.weekNumber;}
	get year () {return this.serviceState.year;}
	get holidays () { return this.serviceState.holidays;}

	getParamsFromRoute () {
		if (router.currentRoute.value.name !== 'TimeTicketWeek') return;
		this.serviceState.year = +router.currentRoute.value.params.year;
		this.serviceState.weekNumber = +router.currentRoute.value.params.weekNumber;
	}

	weekDays () {
		if (this.serviceState.weekNumber && this.serviceState.year)
			return getWeekDays(this.serviceState.weekNumber, this.serviceState.year);
		return [];
	}

	triggerSavedNotif () {
		this.debouncedNotif();
	}

	async setDayNightSettingAsync () {
		this.serviceState.dayNight = await this.self.getUserSettingAsync(SETTING_TYPE.NightHours);
	}

	async setUnfoldSettingAsync () {
		this.serviceState.unfold = await this.self.getUserSettingAsync(SETTING_TYPE.UnfoldTimeTicket);
	}

	async setShowFlexSettingAsync () {
		this.serviceState.showFlex = await this.self.getUserSettingAsync(SETTING_TYPE.ShowFlex);
	}

	async updateNightHoursSettingAsync () {
		await this.self.updateUserSettingAsync({
			settingType: SETTING_TYPE.NightHours,
			value: this.serviceState.dayNight,
		});
	}

	async updateShowFlexSettingAsync (showFlex: boolean) {
		await this.self.updateUserSettingAsync({
			settingType: SETTING_TYPE.ShowFlex,
			value: showFlex,
		});
	}

	triggerMissionEnd (timeTicketWeek: TimeTicketWeekTempWorkerEntity | TimeTicketWeekEntity) {
		useModal({
			title: `Fin de mission`,
			Nested: TimeTicketMissionEndModal,
			NestedProps: { timeTicketWeek },
			size: 'xs',
			events:
				{
					'missionEnded': async (Modal) => {
						Bus.emit('TimeTicketWeekSectionSetupService:refresh-list');
						Bus.emit('TimeTicketWeekSectionSetupService:refresh-details');
						Bus.emit(`TimeTicketWeekPagedListViewSetupService:refresh-list`);
						Modal.close();
					},
				},
		});
	}

	async cancelMissionEndAsync (timeTicketWeekId: number) {
		if (await useConfirm(`Confirmez-vous l'annulation de la fin de mission ?`)) {
			useLoader().show(`Annulation de la fin de mission...`);
			await TimeTicketWeekApiClient.update(timeTicketWeekId, { endOfMissionDate: undefined });
			Bus.emit('TimeTicketWeekSectionSetupService:refresh-list');
			Bus.emit('TimeTicketWeekSectionSetupService:refresh-details');
			Bus.emit(`TimeTicketTempWorkerStickerSetupService:missionEnd:${timeTicketWeekId}`, undefined);
			Bus.emit(`TimeTicketWeekPagedListViewSetupService:refresh-list`);
			useLoader().hide();
			useNotif.success(`La fin de mission a bien été annulée`);
		}
	}

	async validateTempWorkerTimeTicketAsync (timeTicketWeek: TimeTicketWeekTempWorkerEntity) {
		useModal({
			Nested: TimeTicketWeekConfirmModal,
			size: 'xs',
			title: 'Confirmation',
			NestedProps: {
				attachmentsLength: timeTicketWeek.attachments?.length,
				comment: timeTicketWeek.tempWorkerComment,
			},
			actions: [{
				label: 'Oui',
				color: 'success',
				'callback': async (Modal) => {
					await TimeTicketWeekApiTempWorker.validate(timeTicketWeek.id);
					Bus.emit('TimeTicketTempworkerSetupService:remove', timeTicketWeek.publicState);
					Bus.emit('TimeTicketWeekSectionSetupService:refresh-details');
					Modal.close();
				},
			}, {
				label: 'Non',
				outline: true,
				color: 'danger',
				callback: (Modal) => { Modal.close(); },
			}],
		});
	}

	async validateTimeTicketAsync (id: number) {
		if (!this.self.can('TimeTicketValidate')) return;

		if (await useConfirm(`Confirmez-vous la validation de ce relevé ? Il ne pourra plus être modifié`)) {
			try {
				if (this.self.isClient()) {
					await TimeTicketWeekApiClient.validate(id);
					Bus.emit(`TimeTicketTempWorkerDetailsSetupService:ClientValidation:${id}`, id);
				}
				useNotif.success(`Le relevé d'heures a bien été validé`);
			} catch (e: any) {
				useNotif.danger(e?.response?.data ?? `Une erreur est survenue dans la validation du relevé d'heures`);
			}
		}
	}

	async bulkValidateTimeTicketAsync (timeTickets: ITimeTicketWeekDetailForClientModel[]) {
		if (!this.self.can('TimeTicketValidate')) return;

		let confirmMessage = `Confirmez-vous la validation de ce relevé ? Il ne pourra plus être modifié`;
		let notifMessage = `Le relevé d'heures a bien été validé`;
		if (timeTickets.length > 1) {
			confirmMessage = `Confirmez-vous la validation de ces relevés ? Ils ne pourront plus être modifiés`;
			notifMessage = `Les relevés d'heures ont bien été validés`;
		}

		if (this.self.isClient() && await useConfirm(confirmMessage)) {
			try {
				const timeTicketIds = timeTickets.map(x => x.id);
				await TimeTicketWeekApiClient.validateMultiple(timeTicketIds as unknown as IValidateTimeTicketWeekModel);
				timeTickets.forEach((x) => {
					Bus.emit(`TimeTicketTempWorkerStickerSetupService:toggleTimeTicketValidation:${x.id}`);
				});
				useNotif.success(notifMessage);
			} catch (e: any) {
				useNotif.danger(e?.response?.data ?? `Une erreur est survenue dans la validation du relevé d'heures`);
			}
		}
	}

	async getActiveWeekDataAsync (from: Date, to: Date) {
		try {
			useLoader().show(`Chargement de vos relevés d'heures...`);
			const { data } = await TimeTicketWeekApiTempWorker.listDetail(from, to);
			return data.map(x => ({
				...x,
				days: orderBy(x.days, 'date'),
			}));
		} finally {
			useLoader().hide();
		}
	}

	viewContract (timeTicketTempWorkerContractId: number) {
		const routeData = router.resolve({
			name: 'TempWorkerContractDetails',
			params: { id: timeTicketTempWorkerContractId },
		});

		window.open(routeData.href, '_blank');
	}

	viewClientContract (timeTicketClientContractId: number) {
		const routeData = router.resolve({
			name: 'ClientContractDetails',
			params: { id: timeTicketClientContractId },
		});

		window.open(routeData.href, '_blank');
	}

	async viewAsync (items: {id: number}[]) {
		try {
			useLoader().show(`Préparation de la visualisation...`);

			const queryStr = idsQuery(items);

			if (queryStr) {
				if (this.self.isAgency()) {
					await TimeTicketWeekApiAgency.view(queryStr);
				} else if (this.self.isClient()) {
					await TimeTicketWeekApiClient.view(queryStr);
				} else if (this.self.isTempWorker()) {
					await TimeTicketWeekApiTempWorker.view(queryStr);
				}
			}
		} finally {
			useLoader().hide();
		}
	}

	async downloadAsync (items: {id: number}[]) {
		try {
			useLoader().show(`Préparation du téléchargement...`);

			const queryStr = idsQuery(items);

			if (queryStr) {
				if (this.self.isAgency()) {
					await TimeTicketWeekApiAgency.download(queryStr);
				} else if (this.self.isClient()) {
					await TimeTicketWeekApiClient.download(queryStr);
				} else if (this.self.isTempWorker()) {
					await TimeTicketWeekApiTempWorker.download(queryStr);
				}
			}
		} finally {
			useLoader().hide();
		}
	}

	async sendMailAsync (selected: ITimeTicketWeekDetailForAgencyModel[]) {
		await useEmailService().sendEmailAsync(
			{
				sendToUserType: USER_TYPE.Client,
				recipients: uniqBy(
					selected.map(x => ({
						id: x.clientCompanyId,
						label: x.clientCompany?.name,
					})),
					'id',
				),
			},
			DOCUMENT_TYPE.TimeTicket,
			selected.map(x => x.id),
		);

		useNotif.success(`C'est parti !`, `Votre email a bien été envoyé`);
	}

	openAsideTimeTicketAttachment (timeTicket: TimeTicketWeekTempWorkerEntity | TimeTicketWeekEntity) {
		useAside({
			Nested: TimeTicketAttachmentAside,
			NestedProps: { timeTicketWeek: timeTicket },
		});
	}

	async overrideTimeTicketAsync (timeTicketWeekId: number) {
		await TimeTicketWeekApiClient.changeStatus(timeTicketWeekId);
	}

	async updateCommentAsync (...args: Parameters<typeof TimeTicketWeekApiTempWorker.updateComment>) {
		if (this.self.isTempWorker()) {
			const { data } = await TimeTicketWeekApiTempWorker.updateComment(...args);
			return data;
		}
	}

	async detailForContractAsync (...args: Parameters<typeof TimeTicketWeekApiTempWorker.detailForContract>) {
		if (this.self.isTempWorker()) {
			const { data } = await TimeTicketWeekApiTempWorker.detailForContract(...args);
			return data;
		}
		return [];
	}

	async countToFillAsync (...args: Parameters<typeof TimeTicketWeekApiTempWorker.countToFill>) {
		if (this.self.isTempWorker()) {
			const { data } = await TimeTicketWeekApiTempWorker.countToFill(...args);
			return data;
		} else if (this.self.isClient()) {
			const { data } = await TimeTicketWeekApiClient.countToFill(...args);
			return data;
		}
		return 0;
	}

	async getHolidaysAsync (year?: number) {
		if (!year && !this.serviceState.year) return;
		const { data } = await GouvApi.getHolidays(year ?? this.serviceState.year);
		this.serviceState.holidays = data;
	}
}

const timeTicketWeekServiceSingleton = new TimeTicketWeekService();

export default function useTimeTicketWeekService () {
	return timeTicketWeekServiceSingleton;
}
